/**
 * 리포트 시드와 집계 계산.
 *
 * 집계를 만드는 쪽(handler 의 POST)과 시드를 깔는 쪽이 같은 함수를 쓴다 — 두 곳에 같은 계산을
 * 들고 있다가 어긋나지 않게. db 를 import 하지 않고 필요한 테이블을 인자로 받는다(db.ts 가 이
 * 모듈을 쓰므로 순환이 된다).
 */
import type {
  DailyCategory,
  Report,
  ReportEvidence,
  ReportPayload,
  ReportProjectSection,
  ReportStory,
  ReportStorySection,
  Staff,
} from "@/types";
import { PAYLOAD_VERSION } from "@/types";
import type { MockDaily } from "./dailies";
import type { MockPhase, MockProject } from "./projects";
import { today } from "@/lib/dates";
import { SEED_TODAY } from "./seed";

const DAY = 86_400_000;

/** 집계 재료 — handler 는 db 를, 시드는 시드 배열을 넘긴다. */
export interface ReportSources {
  dailies: MockDaily[];
  dailyCategories: DailyCategory[];
  projects: MockProject[];
  /** 그 기간에 완료 처리한 단계를 세는 데 쓴다 */
  projectPhases: MockPhase[];
  users: Staff[];
}

const sum = (values: number[]) => values.reduce((a, b) => a + b, 0);
const round = (value: number) => Math.round(value * 100) / 100;

export const shiftDay = (date: string, days: number) =>
  new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10);

export const spanDays = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY);

/** 집계는 제출한 Daily 만 쓴다 — 임시저장은 아직 보고된 숫자가 아니다. */
const submittedBetween = (src: ReportSources, from: string, to: string): MockDaily[] =>
  src.dailies.filter(
    (daily) => daily.status === "submitted" && daily.work_date >= from && daily.work_date <= to
  );

function hoursOf(dailies: MockDaily[]) {
  const regular = round(sum(dailies.map((d) => d.regular_hours)));
  const overtime = round(sum(dailies.map((d) => d.overtime_hours)));
  return { regular, overtime, total: round(regular + overtime) };
}

function projectSection(
  src: ReportSources,
  projectId: string,
  dailies: MockDaily[],
  from: string,
  to: string
): ReportProjectSection {
  const project = src.projects.find((p) => p.id === projectId);
  const lines = dailies.flatMap((daily) =>
    daily.rows
      .filter((row) => row.project_id === projectId)
      .map((row) => ({ user_id: daily.user_id, row }))
  );
  const members = [...new Set(lines.map((line) => line.user_id))]
    .map((userId) => {
      const theirs = lines.filter((line) => line.user_id === userId).map((line) => line.row);
      return {
        user_id: userId,
        user_name: src.users.find((u) => u.id === userId)?.name ?? userId,
        hours: round(sum(theirs.map((row) => row.actual_hours))),
        plan_hours: round(sum(theirs.map((row) => row.plan_hours))),
        descriptions: [...new Set(theirs.map((row) => row.description.trim()).filter(Boolean))],
      };
    })
    .sort((a, b) => a.user_name.localeCompare(b.user_name, "ko"));
  // 그 기간에 완료 처리한 단계 — 마일스톤을 대신한다.
  const milestones = src.projectPhases.filter(
    (phase) =>
      phase.project_id === projectId &&
      phase.done_date !== null &&
      phase.done_date >= from &&
      phase.done_date <= to
  );
  const hours = round(sum(members.map((m) => m.hours)));
  const planHours = round(sum(members.map((m) => m.plan_hours)));
  return {
    project_id: projectId,
    code: project?.code ?? null,
    name: project?.name ?? "",
    color: project?.color ?? "",
    status: project?.status ?? null,
    hours,
    plan_hours: planHours,
    plan_achievement: planHours ? round(hours / planHours) : null,
    milestones_done: milestones.length,
    members,
  };
}

/** 기간과 프로젝트로 집계 한 덩어리. 만드는 시점에 굳는 값이다. */
export function buildReportPayload(
  src: ReportSources,
  from: string,
  to: string,
  projectIds: string[]
): ReportPayload {
  const dailies = submittedBetween(src, from, to);
  const span = spanDays(from, to);
  const previous = hoursOf(submittedBetween(src, shiftDay(from, -span - 1), shiftDay(from, -1)));
  // 비프로젝트는 project_id 가 없는 줄이다 — 분류가 정하지 않는다.
  const nonProjectRows = dailies.flatMap((daily) =>
    daily.rows.filter((row) => row.project_id === null)
  );
  return {
    version: PAYLOAD_VERSION,
    period: { from, to },
    hours: {
      ...hoursOf(dailies),
      previous_total: previous.total,
      previous_overtime: previous.overtime,
    },
    projects: projectIds.map((id) => projectSection(src, id, dailies, from, to)),
    non_project: src.dailyCategories
      .map((category) => ({
        category_id: category.id,
        name: category.name,
        hours: round(
          sum(
            nonProjectRows
              .filter((row) => row.category_id === category.id)
              .map((row) => row.actual_hours)
          )
        ),
      }))
      .filter((section) => section.hours > 0),
  };
}

/* ── AI 가 쓴 글 흉내 ──────────────────────────────────────────────────────
 *
 * 진짜 글은 서버의 AI 가 쓴다(규약은 다시 정할 예정). mock 은 굳은 집계와 그 기간의 Daily 로
 * 정해진 문장틀을 채워 비슷한 모양을 만든다 — 같은 시드면 같은 글이 나온다.
 */

const WEEKDAY = ["일", "월", "화", "수", "목", "금", "토"];
const dayLabel = (date: string) => {
  const d = new Date(`${date}T00:00:00Z`);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()} (${WEEKDAY[d.getUTCDay()]})`;
};

/** 받침이 있으면 앞, 없으면 뒤 — 「은/는」「이/가」「을/를」「과/와」. */
function josa(word: string, withFinal: string, without: string) {
  const code = word.charCodeAt(word.length - 1);
  if (code < 0xac00 || code > 0xd7a3) return word + without;
  return word + ((code - 0xac00) % 28 ? withFinal : without);
}

const names = (list: string[]) =>
  list.length <= 2 ? list.join("과 ") : `${list.slice(0, 2).join(", ")} 외 ${list.length - 2}명`;

function planLine(hours: number, plan: number) {
  if (!plan) return "계획 없이 올린 일이 대부분이다.";
  const diff = round(hours - plan);
  if (Math.abs(diff) < 1) return `계획(${plan}시간)대로 갔다.`;
  return diff > 0
    ? `계획(${plan}시간)보다 ${diff}시간 더 들었다.`
    : `계획(${plan}시간)보다 ${-diff}시간 덜 들었다.`;
}

function evidenceOf(src: ReportSources, dailies: MockDaily[], projectId: string): ReportEvidence[] {
  return dailies
    .flatMap((daily) =>
      daily.rows
        .filter((row) => row.project_id === projectId && row.description)
        .map((row) => ({ daily, row }))
    )
    .sort((a, b) => b.row.actual_hours - a.row.actual_hours)
    .slice(0, 2)
    .map(({ daily, row }) => ({
      daily_row_id: row.id,
      work_date: daily.work_date,
      user_name: src.users.find((u) => u.id === daily.user_id)?.name ?? daily.user_id,
      title: row.plan_description || row.description,
      category_name: row.category_name,
      plan_hours: row.plan_hours,
      actual_hours: row.actual_hours,
      description: row.description,
      completed_at: row.completed_at,
    }));
}

function nextOf(src: ReportSources, projectId: string, to: string) {
  const next = src.projectPhases
    .filter((p) => p.project_id === projectId && !p.done_date && p.due_date && p.due_date > to)
    .sort((a, b) => (a.due_date! < b.due_date! ? -1 : 1))[0];
  return next
    ? `${josa(next.name, "은", "는")} ${dayLabel(next.due_date!)} 까지다. 그 준비가 이어진다.`
    : "정해진 다음 일정은 아직 없다.";
}

function storySection(
  src: ReportSources,
  section: ReportProjectSection,
  dailies: MockDaily[],
  to: string
): ReportStorySection {
  const people = section.members.map((m) => m.user_name);
  const work = [...new Set(section.members.flatMap((m) => m.descriptions))].slice(0, 2);
  const body = [
    `${josa(names(people), "이", "가")} ${section.hours}시간을 썼다.`,
    planLine(section.hours, section.plan_hours),
    work.length ? `주로 ${work.join(", ")} 일을 했다.` : "",
    section.milestones_done ? `이 기간에 단계 ${section.milestones_done}건을 마쳤다.` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const diff = round(section.hours - section.plan_hours);
  return {
    project_id: section.project_id,
    topic:
      section.plan_hours && Math.abs(diff) >= 1
        ? `${section.hours}시간, 계획보다 ${Math.abs(diff)}시간 ${diff > 0 ? "더" : "덜"}`
        : `${section.hours}시간 · ${work[0] ?? "기록 없음"}`,
    body,
    next: nextOf(src, section.project_id, to),
    // 사람마다 한 일을 앞의 세 가지로 줄여 한 줄에 — 실제로는 AI 가 Daily 를 읽고 쓴다.
    members: section.members.map((m) => ({
      user_id: m.user_id,
      summary: m.descriptions?.length
        ? `${m.descriptions.slice(0, 3).join(", ")}${m.descriptions.length > 3 ? " 등" : ""}`
        : "",
    })),
    evidence: evidenceOf(src, dailies, section.project_id),
  };
}

/** 굳은 집계와 그 기간의 Daily 로 보고서 글을 만든다. 시간이 하나도 없으면 null. */
export function buildReportStory(
  src: ReportSources,
  payload: ReportPayload,
  from: string,
  to: string
): ReportStory | null {
  const { hours } = payload;
  if (!hours.total) return null;
  const dailies = submittedBetween(src, from, to);
  const ranked = [...payload.projects].filter((p) => p.hours > 0).sort((a, b) => b.hours - a.hours);
  const people = new Set(dailies.map((d) => d.user_id)).size;
  const diff = round(hours.total - hours.previous_total);
  const vsPrevious =
    diff === 0
      ? "앞 기간과 같다."
      : `앞 기간보다 ${Math.abs(diff)}시간 ${diff > 0 ? "많다" : "적다"}.`;

  // 연장이 가장 많았던 날
  const byDay = new Map<string, number>();
  for (const d of dailies) byDay.set(d.work_date, (byDay.get(d.work_date) ?? 0) + d.overtime_hours);
  const [busyDay, busyOvertime] = [...byDay].sort((a, b) => b[1] - a[1])[0] ?? ["", 0];

  const [first, second] = ranked;
  const points = [
    `${people}명이 ${hours.total}시간을 썼다. ${vsPrevious}`,
    first
      ? `${first.name}에 가장 많은 ${first.hours}시간이 들었다. ${planLine(first.hours, first.plan_hours)}`
      : "",
    hours.overtime
      ? `연장은 ${hours.overtime}시간(${Math.round((hours.overtime / hours.total) * 100)}%)이었고, ${dayLabel(busyDay)}에 ${round(busyOvertime)}시간으로 가장 많았다.`
      : "연장은 없었다.",
  ].filter(Boolean);

  // 제목 — 그 기간에 가장 눈에 띄는 것 하나를 고른다: 계획을 크게 넘긴 곳, 크게 덜 쓴 곳, 연장.
  const over = ranked.find((p) => p.plan_hours && p.hours - p.plan_hours >= 8);
  const under = ranked.find((p) => p.plan_hours && p.plan_hours - p.hours >= 5);
  const overtimeShare = hours.total ? hours.overtime / hours.total : 0;
  const headline = !first
    ? `${people}명이 ${hours.total}시간을 쓴 기간`
    : over && under
      ? `${josa(over.name, "은", "는")} 계획보다 ${round(over.hours - over.plan_hours)}시간 더 들었고, ${josa(under.name, "은", "는")} 덜 썼다`
      : over
        ? `${josa(over.name, "이", "가")} 계획을 ${round(over.hours - over.plan_hours)}시간 넘겼다`
        : overtimeShare >= 0.1
          ? `연장이 ${Math.round(overtimeShare * 100)}%에 이르렀고, 가장 바쁜 곳은 ${first.name}였다`
          : second
            ? `${first.name}에 시간이 가장 많이 들었고, ${josa(second.name, "이", "가")} 뒤를 이었다`
            : `${first.name}에 시간이 몰린 기간`;

  // 살펴볼 것 — 연장이 가장 많은 사람, 계획 없이 올린 일
  const overtimeBy = new Map<string, number>();
  for (const d of dailies)
    overtimeBy.set(d.user_id, (overtimeBy.get(d.user_id) ?? 0) + d.overtime_hours);
  const [heavyId, heavyHours] = [...overtimeBy].sort((a, b) => b[1] - a[1])[0] ?? ["", 0];
  const unplanned = dailies.flatMap((d) => d.rows).filter((row) => !row.plan_description).length;
  const heavyName = src.users.find((u) => u.id === heavyId)?.name;
  const watch = [
    heavyName && heavyHours >= 3
      ? `${heavyName}의 연장이 ${round(heavyHours)}시간으로 가장 많다.`
      : "",
    unplanned ? `계획 없이 올린 작업이 ${unplanned}건이다.` : "",
  ].filter(Boolean);

  return {
    headline,
    summary: points.slice(0, 2).join(" "),
    points,
    sections: ranked.map((section) => storySection(src, section, dailies, to)),
    watch,
  };
}

/** 리포트 한 건 — 집계와 글을 같은 시점에 굳힌다. */
export function buildReport(
  src: ReportSources,
  base: Omit<Report, "payload" | "story" | "money_visible">,
  projectIds: string[]
): Report {
  const payload = buildReportPayload(src, base.period_from, base.period_to, projectIds);
  return {
    ...base,
    payload,
    story: buildReportStory(src, payload, base.period_from, base.period_to),
    money_visible: false,
  };
}

/** 그 날이 든 주의 월요일. */
const mondayOf = (date: string) => {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  return shiftDay(date, -((day + 6) % 7));
};

/**
 * 지난 네 주의 자동 주간 리포트, 9월 한 달(관리자만), 10월 앞 두 주를 요청을 붙여 직접 만든 것.
 */
export function seedReports(src: ReportSources): Report[] {
  const openProjects = src.projects
    .filter((p) => p.status !== "done" && p.status !== "cancelled")
    .map((p) => p.id);
  const admin = src.users.find((u) => u.role === "admin");
  // Daily 시드와 같은 「오늘」 — 시드가 끝난 뒤에 열어도 지난주 리포트가 있게.
  const thisMonday = mondayOf(today() > SEED_TODAY ? today() : SEED_TODAY);
  const weekly = [1, 2, 3, 4].map((ago) => {
    const from = shiftDay(thisMonday, -7 * ago);
    const to = shiftDay(from, 6);
    const at = `${shiftDay(to, 1)}T09:00:00+09:00`;
    return buildReport(
      src,
      {
        id: `report-weekly-${ago}`,
        requested_by: null,
        schedule_id: "report-schedule-weekly",
        status: "ready",
        period_from: from,
        period_to: to,
        generated_at: at,
        failure_reason: "",
        created_at: at,
        comments:
          ago === 1
            ? [
                {
                  id: "report-comment-1",
                  project_id: null,
                  parent_id: null,
                  author_id: admin?.id ?? "",
                  body: "지난주 연장이 늘었습니다. 월요일 회의에서 프로젝트별로 짚겠습니다.",
                  created_at: `${shiftDay(to, 1)}T09:10:00+09:00`,
                  updated_at: null,
                },
              ]
            : [],
      },
      openProjects
    );
  });
  return [
    ...weekly,
    buildReport(
      src,
      {
        id: "report-monthly-1",
        requested_by: admin?.id ?? null,
        schedule_id: null,
        status: "ready",
        period_from: "2026-09-01",
        period_to: "2026-09-30",
        generated_at: "2026-10-01T10:20:00+09:00",
        failure_reason: "",
        created_at: "2026-10-01T10:20:00+09:00",
        custom_prompt: "발주처 보고용으로, 프로젝트별 다음 달 일정을 꼭 넣어 줘",
        comments: [],
      },
      openProjects.slice(0, 3)
    ),
    buildReport(
      src,
      {
        id: "report-custom-1",
        requested_by: admin?.id ?? null,
        schedule_id: null,
        status: "ready",
        period_from: "2026-10-01",
        period_to: "2026-10-14",
        generated_at: "2026-10-15T11:20:00+09:00",
        failure_reason: "",
        created_at: "2026-10-15T11:20:00+09:00",
        custom_prompt: "착수 단계에서 계획이 어긋난 곳 위주로",
        comments: [],
      },
      openProjects
    ),
  ];
}
